/**
 * Two versions of an integration's settings, compared in sentences.
 *
 * "Status: added FINISHED → Finished", not a JSON diff. Used by History and by
 * the Settings tab before a publish.
 */

import {
  conditionSentence,
  fromSummary,
  stepSummary,
  type FieldSetting,
  type Settings,
  type Step,
} from "@/lib/integrations/settings";

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(nothing)";
  return typeof value === "string" ? value : JSON.stringify(value);
}

function mapChanges(label: string, before: Step | undefined, after: Step | undefined): string[] {
  const was = (before?.values ?? {}) as Record<string, unknown>;
  const now = (after?.values ?? {}) as Record<string, unknown>;
  const out: string[] = [];
  for (const [their, ours] of Object.entries(now)) {
    if (!(their in was)) out.push(`${label}: added ${their} → ${show(ours)}`);
    else if (!same(was[their], ours)) out.push(`${label}: ${their} now → ${show(ours)} (was ${show(was[their])})`);
  }
  for (const their of Object.keys(was)) {
    if (!(their in now)) out.push(`${label}: removed ${their} from the map`);
  }
  if (before && after && before.otherwise !== after.otherwise) {
    out.push(`${label}: anything else now ${after.otherwise ?? "keep"} (was ${before.otherwise ?? "keep"})`);
  }
  return out;
}

function stepsLine(steps: Step[]): string {
  return steps.length ? steps.map(stepSummary).join(" → ") : "no steps";
}

function fieldChanges(label: string, before: FieldSetting | undefined, after: FieldSetting | undefined): string[] {
  if (!before && after) {
    return [`${label}: now from ${fromSummary(after.from)}${after.steps.length ? `, then ${stepsLine(after.steps)}` : ""}`];
  }
  if (before && !after) return [`${label}: no longer mapped`];
  if (!before || !after) return [];
  const out: string[] = [];
  if (!same(before.from, after.from)) {
    out.push(`${label}: from ${fromSummary(after.from)} instead of ${fromSummary(before.from)}`);
  }
  const kinds = (steps: Step[]) => steps.map((step) => step.kind).join(",");
  if (kinds(before.steps) !== kinds(after.steps)) {
    out.push(`${label}: steps now ${stepsLine(after.steps)} (were ${stepsLine(before.steps)})`);
  } else {
    after.steps.forEach((step, index) => {
      const old = before.steps[index];
      if (same(old, step)) return;
      if (step.kind === "map") out.push(...mapChanges(label, old, step));
      else out.push(`${label}: ${stepSummary(step)} (was ${stepSummary(old)})`);
    });
  }
  return out;
}

export function compareSettings(
  before: Settings,
  after: Settings,
  labels: Record<string, string> = {},
): string[] {
  const out: string[] = [];
  const label = (key: string) => labels[key] ?? key;

  if (before.source.jobId !== after.source.jobId) out.push("Uses a different Scout job");
  if (before.source.runs !== after.source.runs) {
    out.push(after.source.runs === "omnium" ? "Reads only runs omnium started" : "Reads every passed run");
  }
  if (before.rows.listPath !== after.rows.listPath) {
    out.push(after.rows.listPath ? `One row is now each entry of ${after.rows.listPath}` : "One row is now each item Scout wrote");
  }
  if (before.keep.mode !== after.keep.mode) {
    out.push(after.keep.mode === "any" ? "Keeps a row when any rule matches" : "Keeps a row only when every rule matches");
  }
  const wasRules = before.keep.conditions.map(conditionSentence);
  const nowRules = after.keep.conditions.map(conditionSentence);
  for (const rule of nowRules) if (!wasRules.includes(rule)) out.push(`Keep rule added: ${rule}`);
  for (const rule of wasRules) if (!nowRules.includes(rule)) out.push(`Keep rule removed: ${rule}`);

  const keys = [...new Set([...Object.keys(before.fields), ...Object.keys(after.fields)])];
  for (const key of keys) out.push(...fieldChanges(label(key), before.fields[key], after.fields[key]));

  if ((before.match.idSource ?? "") !== (after.match.idSource ?? "")) {
    out.push(`Their ids now come from ${show(after.match.idSource)} (was ${show(before.match.idSource)})`);
  }
  if (before.match.matchBy !== after.match.matchBy) {
    out.push(`Finds a record by ${after.match.matchBy} (was ${before.match.matchBy})`);
  }
  if (!same(before.match.keyFields, after.match.keyFields)) {
    out.push(`Rows are the same thing when ${after.match.keyFields.map(label).join(" + ") || "(nothing)"} match`);
  }

  const guards: [keyof Settings["safety"], string][] = [
    ["minShare", "Far fewer rows than usual"],
    ["maxNotPlaced", "Too many rows not placed"],
    ["maxRefused", "Too many rows refused"],
  ];
  for (const [key, words] of guards) {
    const was = before.safety[key] as number;
    const now = after.safety[key] as number;
    if (was !== now) out.push(`${words}: ${percent(now)} (was ${percent(was)})`);
  }
  if (before.safety.writeFrom !== after.safety.writeFrom || before.safety.writeUntil !== after.safety.writeUntil) {
    out.push(
      after.safety.writeFrom || after.safety.writeUntil
        ? `Only writes between ${show(after.safety.writeFrom)} and ${show(after.safety.writeUntil)}`
        : "Writes at any time",
    );
  }
  return out;
}
